// import { Muxer, ArrayBufferTarget } from 'mp4-muxer';

import { LitElement, PropertyValues, ReactiveElement, css, html } from 'lit'
import { customElement, query, state } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js';

import { localized, msg, str } from '@lit/localize'
import { ActionValue, Route, Router } from '@vaadin/router'

import './pages/tfp-page'
import './pages/tfp-unauthorized-page'

import { mediaURLCache } from './models/app-context';
import './components/tfp-modal';
import { TFPModal } from './components/tfp-modal';
import './components/tfp-button'
import { ButtonState } from './components/tfp-button'
import type { TFPCamera } from './components/tfp-camera'
// tfp-camera/tfp-camera-simple/tfp-chat-wgt are loaded lazily (see ensureCamera/ensureChat):
// they must not block the login path, but are prefetched right after an authenticated
// home load so the messages/photo sheets stay instant (2026-09-14).

import { auth } from './services/firebase-service'
import { User, onAuthStateChanged, Unsubscribe, signOut } from 'firebase/auth';
import { commonStyles } from './styles/shared-styles';

import { MemberTypeEnum } from './models/permissionsmodel'
import { apiAuthStateChanged } from './services/trainfitpro-apiservice';
import { localeService } from './services/locale-service';
import { UserService } from './services/users-service'
import { LanguageEnum, ThemeEnum } from './models/settingsmodel';
import { SystemService } from './services/system-service';
import { profileImageParams } from './services/utilities';



import * as Sentry from "@sentry/browser";
import { registerServiceWorker } from './services/sw-registration';
import { RoutePendingIndicator } from './services/route-pending';
import { LiveQueryController } from './components/live-query-controller';
import { createSettingsLiveQuery } from './collections/settings-collection';
import { authService } from './services/auth-service';
import { AuthStateController } from './components/auth-state-controller';

const CURRENT_VERSION = 'b10.1';

/**
 * Hides the static boot splash from index.html (2026-09-15). It replaces the
 * old "loading" paragraph that used to live in this component's shadow root:
 * the splash paints with the HTML itself — before this bundle even runs —
 * so the wait now looks like a real screen instead of a blank page. Idempotent;
 * in tests (no index.html) it is a no-op.
 */
function hideBootSplash(): void {
  const splash = document.getElementById('boot-splash');
  if (!splash) {
    return;
  }
  splash.setAttribute('aria-busy', 'false');
  splash.classList.add('boot-hide');
  window.setTimeout(() => splash.remove(), 300); // matches the 250ms fade in index.html
}

Sentry.init({
  dsn: "https://2a60b8449bad92db806f9a0a0e88f098@sentry.trainfitpro.com/3",

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Alternatively, use `process.env.npm_package_version` for a dynamic release version
  // if your build tool supports it.
  release: `trainfitpro@${CURRENT_VERSION}`,

  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,


  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.captureConsoleIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [window.location.origin],
      unmask: ['tfp-modal', 'dialog', '.sentry-unmask'],
      unblock: ['tfp-modal', 'dialog', '.sentry-unblock'],
    }),
    Sentry.reportingObserverIntegration(),
  ],
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/beta\.trainfitpro\.com\/api/],
});

// Register the app-shell service worker (prod builds only). Registration
// failures are reported to Sentry from the application context — the SW
// itself stays un-instrumented by design (2026-09-14 decision).
registerServiceWorker();


@customElement('not-found-route')
export class NotFoundRoute extends LitElement {
  protected render() {
    return html`<p>Did not find the route</p>`;
  }
}

export interface UiStateType {
  back: ButtonState,
  custom1: ButtonState,
  custom1Icon: string,
  custom2: ButtonState,
  custom2Icon: string,
}

type ForceMode = 'auto' | 'light' | 'dark';

@customElement('tfp-app')
@localized()
export class TFPApp extends LitElement {

  @state()
  navMenuOpen = false;

  // Make sure that component is updated when auth state changes
  // @ts-expect-error to avoid warning about unused, this is here for its side effects.
  private authListener = new AuthStateController(this);

  private settingsLiveQuery = new LiveQueryController(
    this,
    authService,
    () => ({ // init
      collection: createSettingsLiveQuery(),
    }),
    (collection) => {
      const settings = collection.values().next().value;
      if (settings) {
        // TODO: For now hardcoded to dark mode
        const mapToMode = new Map<ThemeEnum, ForceMode>([[ThemeEnum.auto, 'auto'], [ThemeEnum.light, 'light'], [ThemeEnum.dark, 'dark']]);
        document.getElementsByTagName('html')[0].setAttribute('data-theme', mapToMode.get(ThemeEnum.dark)!);
        localeService.setLocale(settings.language);
      }
    }
  );

  alwaysStartMondaysMap = [
    6 * 24 * 60 * 60 * 1000, // Sundays
    0, // Mondays
    1 * 24 * 60 * 60 * 1000, // Tuesdays
    2 * 24 * 60 * 60 * 1000, // Wednesdays
    3 * 24 * 60 * 60 * 1000, // Thursdays
    4 * 24 * 60 * 60 * 1000, // Fridays
    5 * 24 * 60 * 60 * 1000, // Saturdays
  ];

  @state()
  ui: UiStateType = { back: 'invisible', custom1: 'invisible', custom1Icon: '', custom2: 'invisible', custom2Icon: '' };

  @query('#profilemenu')
  profileMenu!: TFPModal;

  @query('#messagesmenu')
  messagesMenu!: TFPModal;

  @query('#photomenu')
  photoMenu!: TFPModal;

  @query('#camera')
  camera!: TFPCamera;


  onAuthUnsubscribe: Unsubscribe[] = [];

  router: Router | null = null;

  connectedCallback() {
    super.connectedCallback();

    addEventListener('app-ui-state', this._handleUiState);
    addEventListener('app-button-messages', this._handleMessagesRequest);

    // Perceived-latency (2026-09-16): react to taps immediately even when the
    // route chunk needs downloading — vaadin-router-go fires synchronously in
    // the same task as Router.go()/click/back-forward; the skeleton is retired
    // when the new location commits (or the navigation errors). Note these
    // events are dispatched on window (unlike app-ui-state), so they must be
    // registered on window.
    window.addEventListener('vaadin-router-go', this._handleRouteStart);
    window.addEventListener('vaadin-router-location-changed', this._handleRouteSettled);
    window.addEventListener('vaadin-router-error', this._handleRouteSettled);

    // sequence matters here
    this.onAuthUnsubscribe.push(onAuthStateChanged(auth, (user: User | null) => apiAuthStateChanged(user)));
    this.onAuthUnsubscribe.push(onAuthStateChanged(auth, (user: User | null) => this.authStateChanged(user)));

    // set locale to users/browsers default
    localeService.setBestMatchLocale(navigator.languages);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    removeEventListener('app-ui-state', this._handleUiState);
    removeEventListener('app-button-messages', this._handleMessagesRequest);

    window.removeEventListener('vaadin-router-go', this._handleRouteStart);
    window.removeEventListener('vaadin-router-location-changed', this._handleRouteSettled);
    window.removeEventListener('vaadin-router-error', this._handleRouteSettled);
    this._routePending.dispose();

    if (this.onAuthUnsubscribe.length > 0) {
      let usbscr: Unsubscribe | undefined;
      // eslint-disable-next-line no-cond-assign
      while (usbscr = this.onAuthUnsubscribe.pop()) {
        usbscr();
      }
    }
  }

  protected updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('initializationOfAuth') && this.initializationOfAuth) {
      this.initializeRouter();
    }
  }

  protected initializeRouter(): void {
    if (this.router) {
      return;
    }

    this.router = new Router(this.shadowRoot?.querySelector('#mainview') as HTMLElement, {
      baseUrl: '/',
      errorHandler: (error: unknown): ActionValue => {
        console.log('Router error:', error instanceof Error ? error.message : error);
        hideBootSplash(); // never leave an error state covered by the splash
        Router.go('/error');
        return document.createElement('p');
      },
    });

    // First route resolved (including any auth redirects) — retire the boot splash.
    this.router.ready
      .then(() => hideBootSplash())
      .catch((err: unknown) => {
        console.error('Router ready failed:', err);
        hideBootSplash();
      });

    const defaultUi: UiStateType = { back: 'invisible', custom1: 'invisible', custom2: 'invisible', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const evaluationUi: UiStateType = { back: 'enabled', custom1: 'disabled', custom2: 'enabled', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const historyUi: UiStateType = { back: 'enabled', custom1: 'enabled', custom2: 'enabled', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const trainerHomeUi: UiStateType = { back: 'invisible', custom1: 'invisible', custom2: 'invisible', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const trainerUi: UiStateType = { back: 'enabled', custom1: 'invisible', custom2: 'invisible', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const trainerPlanningUi: UiStateType = { back: 'enabled', custom1: 'enabled', custom2: 'enabled', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };
    const planUi: UiStateType = { back: 'enabled', custom1: 'enabled', custom2: 'enabled', custom1Icon: 'arrow_back_ios', custom2Icon: 'arrow_forward_ios' };

    this.router.setRoutes([
      {
        path: '/', component: 'tfp-home-page',
        action: this.viewTransitionWrapper(defaultUi, this.isAuth, async () => import('./pages/client/home-page/tfp-home-page'))
      },
      {
        path: '/error', component: 'tfp-error-page',
        action: this.viewTransitionWrapper(trainerUi, () => undefined, async () => import('./pages/tfp-error-page'))
      },
      {
        path: '/login', component: 'tfp-login-page',
        action: this.viewTransitionWrapper(defaultUi, () => undefined, async () => import('./pages/tfp-login-page'))
      },
      {
        path: '/signuptotrainer', component: 'tfp-trainer-signup-page',
        action: this.viewTransitionWrapper(defaultUi, () => undefined, async () => import('./pages/trainer/tfp-trainer-signup-page'))
      },
      {
        path: '/history', component: 'tfp-history-page',
        action: this.viewTransitionWrapper(historyUi, this.isAuth, async () => import('./pages/client/tfp-history-page'))
      },
      {
        path: '/evaluation', component: 'tfp-evaluation-page',
        action: this.viewTransitionWrapper(evaluationUi, this.isAuth, async () => import('./pages/client/tfp-evaluation-page'))
      },
      {
        path: '/anamnesis', component: 'tfp-client-anamnesis-page',
        action: this.viewTransitionWrapper(evaluationUi, this.isAuth, async () => import('./pages/client/tfp-client-anamnesis'))
      },
      {
        path: '/testpage', component: 'tfp-test-page',
        action: this.viewTransitionWrapper(evaluationUi, this.isAuth, async () => import('./pages/tfp-test-page'))
      },
      {
        path: '/trainer', component: 'tfp-trainer-page',
        action: this.viewTransitionWrapper(trainerHomeUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-page'))
      },
      {
        path: '/trainer/onboarding', component: 'tfp-trainer-onboarding-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-onboarding'))
      },
      {
        path: '/trainer/plans', component: 'tfp-trainer-plan-admin-page',
        action: this.viewTransitionWrapper(trainerPlanningUi, this.isTrainerAuth, async () => import('./pages/trainer/plan-admin-page/tfp-trainer-plan-admin-page'))
      },
      {
        path: '/trainer/actionsoon/:mode', component: 'tfp-trainer-actionsoon-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-actionsoon-page'))
      },
      {
        path: '/trainer/activity', component: 'tfp-trainer-activity-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-activity-page'))
      },
      {
        path: '/trainer/financials', component: 'tfp-trainer-financials-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-financials-page'))
      },
      {
        path: '/trainer/clients/:mode', component: 'tfp-trainer-clients-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/clients-page/tfp-trainer-clients-page'))
      },
      {
        path: '/trainer/chat/:mode', component: 'tfp-trainer-chat-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-chat-page'))
      },
      {
        path: '/trainer/clientfocus/:client', component: 'tfp-trainer-client-focus-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-client-focus'))
      },
      {
        path: '/trainer/settings', component: 'tfp-trainer-settings-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-settings-page'))
      },
      {
        path: '/trainer/tasks', component: 'tfp-trainer-tasks-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/tfp-trainer-tasks-page'))
      },
      {
        path: '/trainer/mediaupload', component: 'tfp-trainer-media-page',
        action: this.viewTransitionWrapper(trainerUi, this.isTrainerAuth, async () => import('./pages/trainer/media-page/tfp-trainer-media-page'))
      },
      {
        path: '/plan/:planUUID/:workoutId', component: 'tfp-plan-start-page',
        action: this.viewTransitionWrapper(planUi, this.isReadyForTraining, async () => import('./pages/client/plan-start-page/tfp-plan-start-page'))
      },
      {
        path: '/plan/:planUUID/:workoutId/:exerciseId', component: 'tfp-plan-page',
        action: this.viewTransitionWrapper(planUi, this.isReadyForTraining, async () => import('./pages/client/plan-page/tfp-plan-page'))
      },
      {
        path: '/plan/:planUUID/:workoutId/:exerciseId/summary', component: 'tfp-plan-summary-page',
        action: this.viewTransitionWrapper(planUi, this.isReadyForTraining, async () => import('./pages/client/plan-summary-page/tfp-plan-summary-page'))
      },
      { path: '/unauthorized', component: 'tfp-unauthorized-page' },
      {
        path: '(.*)', component: 'not-found-route',
        action: this.viewTransitionWrapper(defaultUi, () => undefined, () => Promise.resolve())
      },
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  viewTransitionWrapper = (ui: UiStateType, action: any, imp: () => Promise<any>) => {
    return async (context: { pathname: string; route: Route; next: () => void; }, commands: unknown) => {
      await imp(); // dynamic import

      if (!document.startViewTransition || document.visibilityState === 'hidden') {
        // so in case of view transitions not being supported or the document being invisible
        // just skip the animation and perform the action
        return action(context, commands);
      }

      const result = await action(context, commands);

      if (result === undefined) {
        const prom = new Promise((resolve) => {
          const transition = document.startViewTransition({
            update: async () => {
              await this.updateComplete;
              const mainView = this.router?.getOutlet() as ReactiveElement;
              if (mainView) {
                await mainView.updateComplete;
              }

              resolve(result);
            },
            types: ['navigation']
          });

          // Make sure that the promise is always resolved, also if the
          // transition fails, otherwise the router navigation would hang.
          transition.ready.catch((err) => {
            console.error('View transition failed:', err);
            resolve(result);
          });
        });

        // TODO there is uncertainty whether this event is correctly dispatched in the case where the view
        // transition fails, or is not supported
        const event = new CustomEvent('app-ui-state', {
          detail: ui,
        });
        dispatchEvent(event);

        return prom;
      } else {
        return Promise.resolve(result);
      }
    }
  }

  isReadyForTraining = (context: {
    params: { planId?: string }; pathname: string; route: Route; next: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, commands: any) => {
    return this.isAuth(context, commands);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isAuth = (_context: { pathname: string; route: Route; next: () => void; }, commands: any) => {
    return new Promise<void>((resolve) => {
      if (authService.state.isAuth) {
        if (authService.state.user && authService.state.user.memberType === MemberTypeEnum.client) {
          resolve();
        } else {
          resolve(commands.redirect('/trainer'));
        }
      } else {
        resolve(commands.redirect('/login'));
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isAuth2 = async (_context: { pathname: string; route: Route; next: () => void; }, commands: any) => {
    await authService.waitForInit();

    if (authService.state.isAuth) {
      if (authService.state.user && authService.state.user.memberType === MemberTypeEnum.client) {
        return;
      } else {
        return commands.redirect('/trainer');
      }
    } else {
      return commands.redirect('/login');
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isTrainerAuth = (_context: { pathname: string; route: Route; next: () => void; }, commands: any) => {
    return new Promise<void>((resolve) => {
      if (authService.state.isAuth) {
        if (authService.state.user && authService.state.user.memberType === MemberTypeEnum.trainer) {
          resolve();
        } else {
          resolve(commands.redirect('/'));
        }
      } else {
        resolve(commands.redirect('/login'));
      }
    });
  }

  @state()
  initializationOfAuth: boolean = false;

  authStateChanged(user: User | null) {
    this.initializationOfAuth = true;
    this.requestUpdate();

    if (user) {
      // Warm the on-demand shell widgets (chat sheet / camera) in parallel with
      // the home-page boot so opening either sheet stays instant.
      void this.ensureChat();
      void this.ensureCamera();

      const { displayName, email, photoURL, uid } = user;
      const [firstName, ...restName] = displayName!.split(' ');
      const lastName = restName.join(' ');

      Sentry.setUser({ email: email!, id: uid });

      SystemService.signIn({
        providerUserUUID: user.uid,
        userUUID: uid,
        firstName,
        lastName,
        email: email!,
        memberType: MemberTypeEnum.client,
        img: photoURL ?? undefined,
      })
        .then(async (loggedIn) => {
          if (loggedIn) {
            const internalUser = await UserService.getUser(uid)

            authService.setUser(internalUser);

            mediaURLCache.setUserUUID(internalUser.userUUID);
            this.requestUpdate();
          } else {
            authService.setUser(null);
            // mediaURLCache.setUserUUID(null);
            this.requestUpdate();

            await signOut(auth);

            Router.go('/unauthorized');
          }
        })
        .catch((error) => {
          // failure during storage
          console.error(error);
        });
    } else {
      authService.setUser(null);
      // mediaURLCache.setUserUUID(null);
      // Clear a leftover pending-redirect flag so a stale sign-in attempt
      // (e.g. cancelled Google redirect) can't trigger a whitelist check
      // against a later sign-in in the same tab.
      sessionStorage.removeItem('tfp_pending_redirect_signin');
      this.requestUpdate();
    }
  }

  private _handleUiState = (event: Event) => {
    const ev = event as CustomEvent;
    const detail = ev.detail as UiStateType;
    this.ui = detail;
    this.requestUpdate('ui');
  }

  /**
   * Chat and camera modules are intentionally not statically imported: they
   * must never block the login path (2026-09-14 perf work). They load on first
   * use and are prefetched once the user is authenticated (authStateChanged).
   * A failed load is reset so a later tap can retry.
   */
  private chatImport?: Promise<void>;
  private ensureChat = (): Promise<void> => {
    if (!this.chatImport) {
      this.chatImport = import('./widgets/chat/tfp-chat-wgt')
        .then(() => undefined)
        .catch((err: unknown) => {
          this.chatImport = undefined;
          console.error('Failed to load chat widget', err);
        });
    }
    return this.chatImport;
  };

  private cameraImport?: Promise<void>;
  private ensureCamera = (): Promise<void> => {
    if (!this.cameraImport) {
      this.cameraImport = import('./components/tfp-camera-simple')
        .then(() => undefined)
        .catch((err: unknown) => {
          this.cameraImport = undefined;
          console.error('Failed to load camera', err);
        });
    }
    return this.cameraImport;
  };

  /**
   * Opens the messages sheet from anywhere in the app (e.g. the coach card on
   * the client home page), mirroring the bottom-bar chat button.
   */
  private _handleMessagesRequest = async () => {
    if (this.messagesMenu.open) {
      this.messagesMenu.close();
    } else {
      await this.ensureChat();
      this.messagesMenu.show();
    }
  }

  /**
   * Pending-route skeleton (2026-09-16). Armed on vaadin-router-go, retired
   * on location-changed/error; see services/route-pending.ts for the delay.
   */
  private _routePending = new RoutePendingIndicator(() => this.router?.getOutlet());

  private _handleRouteStart = () => this._routePending.navigationStarted();

  private _handleRouteSettled = () => this._routePending.navigationSettled();

  handleBack() {
    const event = new CustomEvent('app-button-back', {
      detail: { message: 'Back' },
    });
    if (authService.state.isAuth) dispatchEvent(event);
  }

  handleBackward() {
    const event = new CustomEvent('app-button-backward', {
      detail: { message: 'Backward' },
    });
    if (authService.state.isAuth) dispatchEvent(event);
  }

  handleForward() {
    const event = new CustomEvent('app-button-forward', {
      detail: { message: 'Forward' },
    });
    if (authService.state.isAuth) dispatchEvent(event);
  }



  @state()
  forceMode: ForceMode = 'auto';

  changeColorScheme = async (newMode: ForceMode) => {
    this.forceMode = newMode;
    const mapToTheme = { auto: ThemeEnum.auto, light: ThemeEnum.light, dark: ThemeEnum.dark }

    if (authService.state.user && this.settingsLiveQuery.isReady) {
      const id = [...this.settingsLiveQuery.collection!.values()][0].version; // fake id
      const tx = this.settingsLiveQuery.collection!.update(id, (draft) => draft.theme = mapToTheme[newMode]);
      await tx.isPersisted.promise;
    }
  }

  changeLocale = async (newLocale: LanguageEnum) => {
    if (authService.state.user && this.settingsLiveQuery.isReady) {
      const id = [...this.settingsLiveQuery.collection!.values()][0].version; // fake id
      const tx = this.settingsLiveQuery.collection!.update(id, (draft) => draft.language = newLocale);
      await tx.isPersisted.promise;
    }
  }

  protected render() {
    // 2026-09-15: the pre-auth "loading" paragraph is gone — the boot splash in
    // index.html now covers the entire wait (HTML → first route resolved), and it
    // paints long before this component's first update could. Rendering the shell
    // early also means #mainview exists the moment the router attaches after auth.
    return html`
    <div class="titlebar">
      ${when(authService.state.isAuth, () => html`
          <tfp-button @tfp-action=${() => this.ui.back !== 'invisible' && this.handleBack()} state=${this.ui.back} type="icon" icon="arrow_back" variant="invisible"></tfp-button>

          <img style="width: 100%; height: 46px; margin: 2px 0px;" src="/trainfitpro.svg"> </img>

          <!--
          <svg style="width: 100%; height: 50px;"
            width="168mm" height="40mm" viewBox="0 0 168 40" xmlns="http://www.w3.org/2000/svg">

            <path id="id-frame" d="M24.568 10.305l118.866-.11M24.633 30.71h118.866"stroke-width="1"
                stroke-linecap="round" stroke-linejoin="round" style="display: none;"/>
            <g id="id-text">
              <path id="id-train"
                  d="M26.18 28.01h2.924l2.43-12.188h4.13l.493-2.45H24.995l-.494 2.45h4.11zm12.87-7.842l.909-4.465h1.896c1.877 0 2.786.534 2.47 2.114-.317 1.56-1.502 2.35-3.379 2.35zm3.477 7.843h3.26l-2.213-5.986c1.798-.652 3.18-2.015 3.615-4.208.672-3.358-1.54-4.445-4.623-4.445H37.49l-2.924 14.639h2.924l1.106-5.532h2.035zm2.418 0h2.964l1.778-3.754h4.721l.277 3.754h3.082l-1.758-14.639H52.55zm5.828-6.026l.751-1.6c.75-1.521 1.482-3.18 2.213-4.78h.079c.118 1.56.177 3.259.316 4.78l.118 1.6zm7.654 6.026h2.924l2.924-14.639H61.35zm5.461 0h2.786l1.165-5.868c.336-1.679.455-3.516.652-5.096h.099l.85 3.14 2.647 7.824h2.983l2.924-14.639h-2.766l-1.166 5.848c-.336 1.66-.474 3.595-.632 5.116h-.099l-.85-3.16-2.686-7.804h-2.983z"
              style="filter: drop-shadow(1px 2px 1px #00a000);"/>
              <path id="id-pro"
                  d="M104.875 28.01h2.924l1.047-5.195h1.956c3.141 0 5.946-1.501 6.618-4.84.692-3.457-1.58-4.603-4.82-4.603h-4.8zm4.426-7.506l.968-4.8h1.66c2.014 0 2.962.572 2.627 2.271-.336 1.68-1.482 2.529-3.497 2.529zm12.177-.336l.91-4.465h1.896c1.877 0 2.785.534 2.47 2.114-.317 1.56-1.502 2.35-3.379 2.35zm3.477 7.843h3.26l-2.212-5.986c1.797-.652 3.18-2.015 3.615-4.208.671-3.358-1.541-4.445-4.623-4.445h-5.077l-2.924 14.639h2.924l1.106-5.532h2.035zm10.203.276c3.872 0 7.132-2.904 8.08-7.665.948-4.741-1.166-7.507-5.038-7.507-3.872 0-7.092 2.766-8.04 7.507-.949 4.761 1.126 7.665 4.998 7.665zm.513-2.528c-2.173 0-3.14-2.015-2.508-5.137.612-3.121 2.35-4.998 4.524-4.998 2.173 0 3.18 1.877 2.568 4.998-.632 3.122-2.41 5.137-4.584 5.137z"
              style="filter: drop-shadow(1px 2px 1px #00a000);"/>
            </g>
            <path id="id-fit"
                d="M77.627 28.01h2.924l1.185-5.926h5.196l.494-2.45H82.23l.75-3.812h6.085l.494-2.45h-9.008zm10.498 0h2.924l2.924-14.638H91.05zm8.366 0h2.923l2.43-12.188h4.13l.493-2.45H95.305l-.494 2.45h4.11z"
            style="filter: drop-shadow(1px 2px 1px #00a000);"/>
            <g id="id-weights">
              <path d="M151.205 10.237h-3.732v20.379h3.732z" />
              <path fill-opacity=".5" d="M159.453 10.237h-3.724v20.379h3.724z" />
              <path fill-opacity=".25" d="M167.439 10.237h-3.724v20.379h3.724z" />
              <path d="M16.397 10.237h3.724v20.379h-3.724z" />
              <path fill-opacity=".5" d="M8.15 10.237h3.724V30.62H8.15z" />
              <path fill-opacity=".25" d="M.164 10.237h3.724V30.62H.164z" />
          -->

              <!--
              <path d="M151.205.297h-3.732v39.707h3.732z" />
              <path fill-opacity=".5" d="M159.453 5.569h-3.724V35.28h3.724z" />
              <path fill-opacity=".25" d="M167.439 10.424h-3.672v20.22h3.672z" />
              <path d="M16.397.134h3.732v39.707h-3.732z" />
              <path fill-opacity=".5" d="M8.15 5.406h3.724v29.711H8.15z" />
              <path fill-opacity=".25" d="M.164 10.262h3.672v20.22H.164z" />
              -->
          <!--
            </g>
          </svg>
          -->
          <div>${CURRENT_VERSION}</div>
        `
    )}
    </div>

    <div part="content" class="contents" id="mainview">
    </div>

    <div class="bottombar">
      ${when(authService.state.isAuth, () => {
      const img = profileImageParams(authService.state.user ?? undefined);
      return html`
            <tfp-button @tfp-action=${async () => {
                // const state = await this.profileMenuInTransitionPromise;

                if (this.profileMenu.open) {
                  this.profileMenu.close();
                } else {
                  this.profileMenu.show();
                }
              }}
              style="padding: 5px 0px; box-sizing: border-box;"
              shape="round"
              type=${img.type}
              .icon=${img.img}
              variant="invisible">
            </tfp-button>
            <tfp-button @tfp-action=${async () => {
                // const state = await this.messagesMenuInTransitionPromise;

                if (this.messagesMenu.open) {
                  this.messagesMenu.close();
                } else {
                  await this.ensureChat();
                  this.messagesMenu.show();
                }
              }}
              type="icon" icon="chat"
              variant="invisible">
            </tfp-button>

            <tfp-button @tfp-action=${async () => {
                // const state = await this.photoMenuInTransitionPromise;
                if (this.photoMenu.open) {
                  this.photoMenu.close();
                } else {
                  await this.ensureCamera();
                  this.photoMenu.show();
                }
                if (!this.camera.streaming) {
                  this.camera.startupCamera();
                } else {
                  this.camera.shutdownCamera();
                }
              }}
              type="icon" icon="camera"
              variant="invisible">
            </tfp-button>

            <tfp-button @tfp-action=${() => this.handleBackward()} state=${this.ui.custom1} type="icon" icon=${this.ui.custom1Icon} variant="invisible">
            </tfp-button>
            <tfp-button @tfp-action=${() => this.handleForward()} state=${this.ui.custom2} type="icon" icon=${this.ui.custom2Icon} variant="invisible">
            </tfp-button>
          `
    }
    )}
    </div>

    <tfp-modal mode="sheet" id="profilemenu"
      @tfp-closed=${() => {
    //   @click=${(_ev: Event) => {
    //     this.profileMenu?.togglePopover(false);
    //     _ev.stopPropagation();
    //   }}
    //   @beforetoggle=${(e: Event) => {
    //     this.profileMenuInTransitionPromise = new Promise<string>((resolve, _reject) => {
    //       e.target!.addEventListener('toggle', (e: Event) => {
    //         const tev = e as ToggleEvent;
    //         resolve(tev.newState)
    //       }, { once: true });
    //     });

        this.profileMenu?.close();
      }}
      >
      <div style="display: grid; grid-template-columns: 1fr; grid-template-rows: min-content; gap: 5px;"
        @click=${(ev: Event) => {
        ev.stopPropagation();
      }}
      >
        <p>${msg(str`Hi ${authService.state.user?.firstName}`)} (${authService.state.user?.email})</p>
        <p>${msg('Settings')}</p>
        ${when(authService.state.user?.memberType == MemberTypeEnum.trainer, () => html`
          <tfp-button style="height: 50px;" @tfp-action=${() => { this.profileMenu?.close(); Router.go('/trainer/settings'); }} type="icon" icon="settings" .text=${msg('Advanced Settings')}></tfp-button>
        `)}
        ${when(authService.state.user?.memberType == MemberTypeEnum.client, () => html`
          <label for="profilepicture">${msg('Change profile picture')}</label>
          <input name="profilepicture" type="file" capture="user" accept="image/*"> </input>
        `)}
        <div @change=${(e: Event) => {
        const newLocale = (e.target as HTMLSelectElement).value as LanguageEnum;
        this.changeLocale(newLocale);
      }}>
          <p>${msg('Language')}</p>
          <p><input type="radio" name="langselect" value="en" id="lang_en" ?checked=${localeService.getLocale().startsWith('en')}> </input> <label for="lang_en">English</label></p>
          <p><input type="radio" name="langselect" value="pt-BR" id="lang_pt_br" ?checked=${localeService.getLocale() === 'pt-BR'}> </input> <label for="lang_pt_br">Português</label></p>
          <p><input type="radio" name="langselect" value="es-419" id="lang_es_419" ?checked=${localeService.getLocale() === 'es-419'}> </input> <label for="lang_es_419">Español</label></p>
        </div>
        <div class="theme-select"
          @change=${async (_e: Event) => {
        // const elm = e.target as HTMLInputElement;
        // await this.changeColorScheme(elm.value as ForceMode);
        // FIXME: temporarily it will always switch to dark mode
        await this.changeColorScheme('dark');
      }}
          >
          <input type="radio" name="themeselect" value="auto" id="theme_auto" ?checked=${this.forceMode == 'auto'}> </input> <label for="theme_auto"><span class="material-symbols-outlined">settings_applications</span></label>
          <input type="radio" name="themeselect" value="light" id="theme_light" ?checked=${this.forceMode == 'light'}> </input> <label for="theme_light"><span class="material-symbols-outlined">light_mode</span></label>
          <input type="radio" name="themeselect" value="dark" id="theme_dark" ?checked=${this.forceMode == 'dark'}> </input> <label for="theme_dark"><span class="material-symbols-outlined">dark_mode</span></label>
        </div>

        <tfp-button style="height: 50px;" @tfp-action=${() => { this.profileMenu?.close(); signOut(auth); authService.setUser(null); Router.go('/login'); }} type="icon" icon="logout" .text=${msg('Logout')}></tfp-button>

      </div>

    </tfp-modal>

    <tfp-modal mode="sheet" id="messagesmenu"
      @tfp-closed=${() => {
        this.messagesMenu?.close();
      }}
      >
      <div style="height: 80vh;"
        @click=${(ev: Event) => {
        ev.stopPropagation();
      }}
      >
        <tfp-chat-wgt></tfp-chat-wgt>
      </div>
    </tfp-modal>

    <tfp-modal mode="sheet" id="photomenu"
      @tfp-closed=${() => {
        this.photoMenu?.close();
        if (this.camera.streaming) {
          this.camera.shutdownCamera();
        }
      }}
      >
      <tfp-camera-simple @click=${(ev: Event) => {
        ev.stopPropagation();
      }}
        id="camera"
      > </tfp-camera-simple>
      <!--
      <tfp-camera id="camera"> </tfp-camera>
      -->
    </tfp-modal>


    `
  }

  mediaExperiment = async (_ev: Event) => {
    /*
    const elm = ev.target as HTMLInputElement;

    if (elm.files) {
      console.log(`${elm.files[0].type}, size: ${elm.files[0].size}`);

      const muxer = new Muxer({
          target: new ArrayBufferTarget(),
          video: {
              codec: 'avc',
              width: 1280,
              height: 720
          },
          fastStart: 'in-memory'
      });

      const videoEncoder = new VideoEncoder({
          output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
          error: e => console.error(e)
      });
      videoEncoder.configure({
          codec: 'avc1.42001f',
          width: 1280,
          height: 720,
          bitrate: 1e6
      });

      // Encode some frames...

      await videoEncoder.flush();
      muxer.finalize();

      let { buffer } = muxer.target; // Buffer contains final MP4 file

    }
    */
  }



  static styles = [commonStyles, css`
    :host {
      box-sizing: border-box;
      max-width: 1280px;
      margin: 0 0;
      padding: 0;
      display: grid;
      text-align: center;
      width: 100%;
      height: 100dvh;
      grid-template-rows: 50px 1fr 50px;
      grid-template-columns: 1fr;
      color: var(--tfp-md-color-on-background);
      background-color: var(--tfp-md-color-background);
    }


    button {
      background-color: inherit;
      color: inherit;
      outline: none;
      border: none;
      padding-inline: 0px;
    }

    .theme-select {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      align-items: center;
      justify-items: center;
      gap: 2px;
      border-radius: 10px;
      /* b11 role fix: the ring+fill behind the segments is a FILL; 'primary' is
         the text role since the Kinetic inversion (lavender in dark mode). */
      border: 2px solid var(--tfp-md-color-brand-fill);
      background-color: var(--tfp-md-color-brand-fill);
    }

    .theme-select > input {
      display: none;
    }

    .theme-select > label {
      width: 100%;
      text-align: center;
      background-color: var(--tfp-md-color-surface);
    }

    .theme-select > label:nth-child(2) {
      border-radius: 10px 0px 0px 10px;
    }

    .theme-select > label:nth-child(6) {
      border-left: unset;
      border-radius: 0px 10px 10px 0px;
    }

    /* b11 role fix: selected segment is a FILL (brand-fill + on-primary ink);
       'primary' is the text role since the Kinetic inversion. */
    .theme-select > input[type="radio"]:checked + label {
      background-color: var(--tfp-md-color-brand-fill);
      color: var(--tfp-md-color-on-primary);
    }



    svg > #id-frame {
      stroke: var(--tfp-md-color-on-background);
    }

    svg > #id-text {
      fill: var(--tfp-md-color-on-background);
    }

    svg > #id-fit {
      fill: var(--tfp-md-color-accent-green);
    }

    svg > #id-weights {
      fill: var(--tfp-md-color-accent-green);
    }




    #profilemenu > div, #messagesmenu > div, #photomenu > tfp-camera, #photomenu > tfp-camera-simple {
      width: 100%;
      box-sizing: border-box;
      max-height: 80vh;
      background-color: var(--tfp-md-color-surface);
      color: var(--tfp-md-color-on-background);
      border-radius: var(--tfp-md-shape-corner-medium);
      /* border: 2px solid var(--tfp-md-color-secondary);*/
      text-align: left;
      padding: 10px;
      overflow-y: auto;
    }

    #profilemenu::part(dialog), #messagesmenu::part(dialog), #photomenu::part(dialog) {
      bottom: 50px;
      border-radius: var(--tfp-md-shape-corner-medium);
    }

    .titlebar, .contents, .bottombar {
      color: var(--tfp-md-color-on-background);
      background-color: var(--tfp-md-color-background);
    }

    .titlebar {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: 50px 1fr 50px;
      align-items: center;
    }

    .titlebar-title {
      display: block;
      width: 100%;
      flex-shrink: initial !important;
      overflow: hidden;
      font-size: 22px;
      font-weight: 400;
      letter-spacing: normal;
      line-height: 2.5rem;
    }

    .titlebar-avatar {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      white-space: nowrap;
      border-radius: 50%;
      -webkit-user-select: none;
      user-select: none;
    }

    .contents {
      display: grid;
      grid-template-rows: minmax(0, 1fr);
      overflow: hidden;
      /* Anchor for the pending-route skeleton (position:absolute inset:0) so it
         covers only the content area, not the titlebar/bottombar (2026-09-16). */
      position: relative;
    }

    .bottombar {
      display: grid;
      grid-template-rows: 1fr;
      grid-template-columns: repeat(5, 1fr);
      grid-gap: 5px;
      align-items: center;
      justify-items: center;
    }

  `]
}

declare global {
  interface HTMLElementTagNameMap {
    'my-app': TFPApp
  }
}
